import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import { MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { useDispatch } from "react-redux";
// LGX-1146: invoice-alert notifications (INVOICE_CREATED/REJECTED/APPROVED) are built from
// the same shipment-update event as shipment alerts, and their `referenceNumber` is the
// SHIPMENT number, not a real invoice id/number (there's no invoice identifier on the
// notification payload at all). "View" therefore opens the related shipment, same as the
// Shipment Alerts tab — previously it dispatched a shipmentSlice.addTab-shaped payload with
// invoiceSlice's addTab into a non-existent "invoiceView" tab type, which silently rendered
// "No Content".
import { addTab } from "../../../../redux/slices/shipmentSlice";
import { getUserNotifications, markNotificationRead, markNotificationUnread } from "../../../../api/notificationsApi";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";

const TABLE_HEADERS = ["Selection", "Action", "Title", "Message", "Status", "Received At"];

// LGX-1146: a notification's shipment may have moved on since it was sent — reassigned to
// a different carrier, whose current rate/pricing details would then be visible on the
// shipment page. These categories mean the shipment/assignment is stale, so "View" is
// disabled rather than risk opening another carrier's data.
const UNSAFE_TO_VIEW_CATEGORIES = new Set([
  "SHIPMENT_UNASSIGNED",
  "SHIPMENT_RECALLED",
  "SHIPMENT_REJECTED",
  "INVOICE_REJECTED",
]);
const UNSAFE_TO_VIEW_TOOLTIP =
  "This shipment's status has changed since this notification was sent — it can no longer be safely opened from here.";

const InvoiceNotificationTable = ({ searchQuery = "", isReadFilter = null, searchTrigger, setTotalNotifications }) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [notificationsPerPage, setNotificationsPerPage] = useState(10);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const dropdownRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const tableContainerRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tableViews = useTableSavedViews({
    pageKey: TABLE_PAGE_KEYS.NOTIFICATION_INVOICE,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Title"],
  });

  const {
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownRow(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close bulk menu when clicking outside
  useEffect(() => {
    const handle = (e) => {
      if (
        bulkMenuRef.current && !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current && !checkboxHeaderRef.current.contains(e.target)
      ) setShowBulkMenu(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // Debounce free-text search so it doesn't fire a request per keystroke
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Reset to page 1 whenever the filters actually change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, isReadFilter]);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getUserNotifications({
        module: "invoice",
        isRead: isReadFilter,
        search: debouncedSearch,
        page: currentPage - 1,
        size: notificationsPerPage,
      });

      const { content = [], totalElements = 0, totalPages: pages = 1 } = response.data;
      setNotifications(content);
      setTotalNotifications(totalElements);
      setTotalPages(pages || 1);
    } catch (error) {
      console.error("Error fetching invoice notifications:", error.response?.data || error.message);
      setTotalNotifications(0);
      setNotifications([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, notificationsPerPage, isReadFilter, debouncedSearch, setTotalNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [searchTrigger, fetchNotifications]);

  const formattedNotifications = notifications.map((n) => ({
    id: n.id,
    referenceNumber: n.notification?.referenceNumber,
    category: n.notification?.category,
    Title: n.notification?.title ?? "-",
    Message: n.notification?.message ?? "-",
    Status: n.isRead ? "Read" : "Unread",
    "Received At": n.deliveredAt ? new Date(n.deliveredAt).toLocaleString() : "-",
  }));

  const handleSelectAll = (checked) => {
    setSelectedNotifications(checked ? formattedNotifications.map((n) => n.id) : []);
  };

  const handleSingleCheckbox = (id) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleViewShipment = (row) => {
    if (!row.referenceNumber || UNSAFE_TO_VIEW_CATEGORIES.has(row.category)) return;
    navigate("/dashboard/shipments");
    dispatch(addTab({
      id: `shipment-${row.referenceNumber}-${Date.now()}`,
      name: `Shipment: ${row.referenceNumber}`,
      contentType: "shipmentView",
      shipmentNumber: row.referenceNumber,
      isDynamic: true,
      createdAt: Date.now(),
    }));
    if (row.Status === "Unread") {
      markNotificationRead(row.id).then(fetchNotifications).catch(() => {});
    }
  };

  const handleToggleRead = (row) => {
    const toggle = row.Status === "Unread" ? markNotificationRead : markNotificationUnread;
    toggle(row.id).then(fetchNotifications).catch((err) =>
      console.error("Failed to toggle notification read state:", err.response?.data || err.message)
    );
  };

  const handleBulkMarkAsRead = async () => {
    const unreadSelected = formattedNotifications
      .filter((n) => selectedNotifications.includes(n.id) && n.Status === "Unread")
      .map((n) => n.id);

    if (!unreadSelected.length) {
      notify.warning({ title: "Nothing to do", message: "Selected notifications are already read." });
      setShowBulkMenu(false);
      return;
    }

    try {
      for (const id of unreadSelected) {
        await markNotificationRead(id);
      }
      notify.success({
        title: "Marked as read",
        message: `${unreadSelected.length} notification(s) marked as read.`,
      });
    } catch (err) {
      console.error("Failed to bulk mark notifications as read:", err.response?.data || err.message);
      notify.error({ title: "Failed", message: "Could not mark all selected notifications as read." });
    } finally {
      await fetchNotifications();
      setSelectedNotifications([]);
      setShowBulkMenu(false);
    }
  };

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === id ? null : id));
  };

  const getBulkMenuStyle = () => {
    const container = tableContainerRef.current;
    const header = checkboxHeaderRef.current;
    if (!container || !header) return null;

    const checkbox = header.querySelector('input[type="checkbox"]');
    const anchor = checkbox ?? header;
    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return {
      top: anchorRect.bottom - containerRect.top + container.scrollTop + 6,
      left: anchorRect.left - containerRect.left + container.scrollLeft,
    };
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className="bg-white flex flex-col h-full">
      <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2">
        <TableSavedViewsControl
          controlLabel={controlLabel}
          views={views}
          activeView={activeView}
          loading={viewsLoading}
          error={viewsError}
          isDirty={isDirty}
          allDataHeaders={TABLE_HEADERS.slice(2)}
          dataHeaders={dataHeaders}
          onSelectView={selectView}
          onSaveNew={saveNewView}
          onUpdateActive={updateActiveView}
          onRename={renameView}
          onDelete={deleteView}
          onSetDefault={setDefaultView}
          onUpdateColumnVisibility={updateColumnVisibility}
          onManageColumns={onManageColumns}
        />
      </div>
      <div ref={tableContainerRef} className="flex-1 min-h-[200px] bg-white sm:min-h-[300px] overflow-x-auto relative">
        {isLoading ? (
          <div className="p-fluid-2 text-gray-600 text-fluid-sm">Loading invoice notifications...</div>
        ) : !formattedNotifications.length ? (
          <div className="p-fluid-2 text-gray-400 text-fluid-sm">No invoice notifications found.</div>
        ) : (
          <Table
            {...columnManagerProps}
            data={formattedNotifications}
            initialColumnWidths={columnWidths}
            onColumnResize={setColumnWidths}
            enableColumnResize
            enableColumnReorder
            enableColumnPin
            selectedRows={selectedNotifications}
            getRowSelectionKey={(row) => row.id}
            totalValidRows={formattedNotifications.length}
            onSelectAll={handleSelectAll}
            renderCheckboxActions={() => setShowBulkMenu((p) => !p)}
            checkboxHeaderRef={checkboxHeaderRef}
          >
            {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
              const orderedDataHeaders = (visibleColumns ?? [])
                .map((col) => col.id)
                .filter((id) => id !== "Selection" && id !== "Action");
              const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;
              const unsafe = UNSAFE_TO_VIEW_CATEGORIES.has(row.category);

              return (
                <>
                  {/* Column 0 — Checkbox */}
                  <td
                    className={`bg-white border-b border-gray-200 z-[10] ${getStickyCellProps("Selection").className}`}
                    style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
                  >
                    <input
                      type="checkbox"
                      className="cursor-pointer mx-auto block"
                      checked={selectedNotifications.includes(row.id)}
                      onChange={() => handleSingleCheckbox(row.id)}
                    />
                  </td>

                  {/* Column 1 — Action */}
                  <td
                    onClick={(e) => e.stopPropagation()}
                    className={`bg-white border-b border-gray-200 text-center z-[10] ${getStickyCellProps("Action").className}`}
                    style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50, boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                  >
                    <button
                      className="p-1 text-gray-500 hover:text-gray-800 w-full"
                      onClick={(e) => handleActionClick(row.id, e)}
                    >
                      <MoreHorizontal size={18} className="mx-auto" />
                    </button>
                  </td>

                  {openDropdownRow === row.id && ReactDOM.createPortal(
                    <div
                      ref={dropdownRef}
                      style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                    >
                      <ActionMenu
                        rowId={row.id}
                        isOpen
                        onClose={() => setOpenDropdownRow(null)}
                        moduleName="Invoice"
                        actions={[
                          {
                            icon: "Eye",
                            label: "View",
                            color: "blue",
                            action: () => handleViewShipment(row),
                            disabled: unsafe,
                            tooltip: unsafe ? UNSAFE_TO_VIEW_TOOLTIP : undefined,
                          },
                          {
                            icon: row.Status === "Unread" ? "Check" : "Undo2",
                            label: row.Status === "Unread" ? "Mark as read" : "Mark as unread",
                            color: "green",
                            action: () => handleToggleRead(row),
                          },
                        ]}
                      />
                    </div>,
                    document.body
                  )}

                  {/* Columns 2+ — Data */}
                  {dataHeadersToRender.map((header) => (
                    <td
                      key={header}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-black text-xs sm:text-sm md:text-base border-b border-gray-200 ${
                        header === "Status" && row.Status === "Unread" ? "font-semibold text-blue-700" : ""
                      } ${getStickyCellProps(header).className}`}
                      onDoubleClick={() => handleViewShipment(row)}
                      style={{
                        ...getStickyCellProps(header).style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "200px",
                      }}
                    >
                      {row[header]}
                    </td>
                  ))}
                </>
              );
            }}
          </Table>
        )}

        {/* Bulk action menu */}
        {showBulkMenu &&
          (() => {
            const bulkMenuStyle = getBulkMenuStyle();
            if (!bulkMenuStyle) return null;

            return (
              <div ref={bulkMenuRef} className="absolute z-[1000]" style={bulkMenuStyle}>
                <ActionMenu
                  rowId="bulk"
                  isOpen
                  onClose={() => setShowBulkMenu(false)}
                  moduleName="Invoice"
                  placement="header"
                  actions={[
                    {
                      icon: "Check",
                      label: "Mark as Read",
                      color: "green",
                      action: handleBulkMarkAsRead,
                    },
                  ]}
                />
              </div>
            );
          })()}
      </div>

      {/* Pagination */}
      <div className="flex justify-between border-t items-center bg-white p-fluid-1">
        <div className="flex items-center">
          <span className="mr-2">Notifications per page</span>
          <select
            value={notificationsPerPage}
            onChange={(e) => {
              setNotificationsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border-b-2 border-black focus:outline-none"
          >
            {[5, 10, 25, 50].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="mx-2 text-gray-500 disabled:opacity-50"
          >
            ❮
          </button>
          <span>
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="mx-2 text-gray-500 disabled:opacity-50"
          >
            ❯
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceNotificationTable;
